import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { track } from '../lib/analytics'
import { api } from '../lib/api'

interface CreatedSession {
  id: string
}

/**
 * The offer screen (01 §3.4): one full-screen decision, two equal exits.
 *
 * Declining records `workspace_declined`, which is a KPI input (01 §9) — the funnel question
 * "how many people are offered the workspace and say no?" is only answerable if the decline is
 * recorded as deliberately as the accept.
 */
export function WorkspaceOffer() {
  const { id: estimateId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // "How many people are shown the offer?" is the denominator for `workspace_declined`, and
  // only the browser can answer it (01 §9).
  useEffect(() => {
    track('workspace_offered')
  }, [])

  const decline = useMutation({
    mutationFn: () => api.post(`/estimates/${estimateId}/decline-workspace`),
    // A failed analytics write must not trap someone on a screen they are trying to leave.
    onSettled: () => navigate('/'),
  })

  // The workspace route takes a *session* id, and the session does not exist yet — this screen
  // is the moment it is created. Navigating to `/w/{estimateId}` (which this button once did)
  // put the estimate's id where a session id belongs, and every homeowner who accepted the
  // offer was told "We could not open that workspace."
  const open = useMutation({
    mutationFn: () => api.post<CreatedSession>('/workspace/sessions', { estimate_id: estimateId }),
    onSuccess: (session) => {
      track('workspace_opened')
      navigate(`/w/${session.id}`)
    },
  })

  return (
    <div className="mx-auto flex min-h-[70dvh] max-w-2xl flex-col justify-center px-5 py-12">
      <p className="eyebrow">Before Andrew visits</p>
      <h1 className="mt-4 text-3xl sm:text-4xl">
        Want to see it in your room before Andrew visits?
      </h1>
      <p className="mt-5 text-lg">
        Take a photo, swap in real products from the stores we buy from, and Andrew sees what
        you picked before he arrives.
      </p>
      <p className="mt-3 text-muted">Takes 5–15 minutes. Free. Your visit is booked either way.</p>

      {open.isError && (
        <p role="alert" className="mt-4 rounded-card border border-line bg-sand px-4 py-3 text-sm">
          That did not open. Give it another try — your visit is booked either way.
        </p>
      )}

      <div className="mt-9 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          disabled={open.isPending}
          onClick={() => open.mutate()}
          className="rounded-btn bg-blue px-8 py-4 text-base font-bold text-white shadow-blue disabled:opacity-60"
        >
          {open.isPending ? 'Opening…' : 'Open workspace'}
        </button>
        <button
          type="button"
          disabled={decline.isPending}
          onClick={() => decline.mutate()}
          className="rounded-btn border border-line-2 bg-white px-8 py-4 text-base font-semibold text-ink disabled:opacity-60"
        >
          No thanks — I’m done
        </button>
      </div>
    </div>
  )
}
